"use client";

import Image from "next/image";
import Link from "next/link";
import { Download, ExternalLink, Mail } from "lucide-react";

import { Container } from "@/components/primitives/Container";
import { Eyebrow } from "@/components/primitives/Eyebrow";
import { Reveal } from "@/components/motion/Reveal";
import { getPress } from "@/content/press";
import { useLocale } from "@/lib/i18n/provider";

export function PressKit() {
  const { locale } = useLocale();
  const press = getPress(locale);

  return (
    <>
      <section className="relative overflow-hidden border-b border-[var(--color-surface-border)] bg-[var(--color-bg-sunken)] py-16 md:py-24">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-[-30%] -z-10 h-[70%]"
          style={{
            background:
              "radial-gradient(55% 55% at 50% 0%, rgba(var(--glow-accent-rgb), 0.16) 0%, transparent 70%)",
          }}
        />
        <Container>
          <Reveal>
            <Eyebrow>{press.hero.eyebrow}</Eyebrow>
            <h1 className="mt-4 max-w-3xl text-[clamp(2.25rem,5vw,3.5rem)] font-bold leading-[1.05] tracking-[-0.03em] text-[var(--color-text-primary)]">
              {press.hero.title}
            </h1>
            <p className="mt-5 max-w-2xl text-[clamp(1rem,1.3vw,1.2rem)] leading-relaxed text-[var(--color-text-secondary)]">
              {press.hero.body}
            </p>
          </Reveal>
        </Container>
      </section>

      <section className="py-16 md:py-24">
        <Container>
          <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
            <Reveal>
              <Eyebrow>{press.boilerplate.eyebrow}</Eyebrow>
              <h2 className="mt-4 text-[clamp(1.75rem,3vw,2.25rem)] font-bold tracking-tight text-[var(--color-text-primary)]">
                {press.boilerplate.title}
              </h2>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="space-y-5 text-[15px] leading-relaxed text-[var(--color-text-secondary)] md:text-base">
                {press.boilerplate.paragraphs.map((paragraph) => (
                  <p key={paragraph.slice(0, 48)}>{paragraph}</p>
                ))}
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      <section className="border-y border-[var(--color-surface-border)] bg-[var(--color-bg-sunken)] py-16 md:py-24">
        <Container>
          <Reveal>
            <Eyebrow>{press.assets.eyebrow}</Eyebrow>
            <h2 className="mt-4 max-w-2xl text-[clamp(1.75rem,3vw,2.25rem)] font-bold tracking-tight text-[var(--color-text-primary)]">
              {press.assets.title}
            </h2>
            <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-[var(--color-text-secondary)]">
              {press.assets.body}
            </p>
          </Reveal>

          <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {press.assets.items.map((item, index) => {
              const Icon = item.kind === "document" ? ExternalLink : Download;
              return (
                <Reveal key={item.href + item.label} delay={0.04 * index}>
                  <li className="h-full rounded-3xl border border-[var(--color-surface-border)] bg-[var(--color-surface-glass)] p-5">
                    {item.kind === "photo" ? (
                      <div className="mb-4 overflow-hidden rounded-2xl border border-[var(--color-surface-border)] bg-[var(--color-bg-raised)]">
                        <Image
                          src={item.href}
                          alt={item.label}
                          width={480}
                          height={480}
                          className="aspect-square h-auto w-full object-cover"
                        />
                      </div>
                    ) : item.kind === "logo" ? (
                      <div className="mb-4 flex h-28 items-center justify-center rounded-2xl border border-[var(--color-surface-border)] bg-[var(--color-bg-raised)] px-6">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          src={item.href}
                          alt={item.label}
                          className="max-h-14 w-auto max-w-full"
                        />
                      </div>
                    ) : null}
                    <h3 className="text-[15px] font-semibold text-[var(--color-text-primary)]">
                      {item.label}
                    </h3>
                    <p className="mt-2 text-[13px] leading-relaxed text-[var(--color-text-secondary)]">
                      {item.description}
                    </p>
                    <Link
                      href={item.href}
                      {...(item.kind === "document"
                        ? {}
                        : {
                            download: true,
                            target: "_blank",
                            rel: "noopener noreferrer",
                          })}
                      className="mt-4 inline-flex items-center gap-2 text-[13px] font-semibold text-[var(--color-accent-base)] underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--color-accent-base)]"
                    >
                      <Icon size={14} aria-hidden />
                      {item.label}
                    </Link>
                  </li>
                </Reveal>
              );
            })}
          </ul>
        </Container>
      </section>

      <section className="py-16 md:py-24">
        <Container>
          <Reveal>
            <div className="rounded-3xl border border-[var(--color-surface-border)] bg-[var(--color-surface-glass)] p-8 md:p-10">
              <Eyebrow>{press.contact.eyebrow}</Eyebrow>
              <h2 className="mt-4 text-[clamp(1.5rem,3vw,2rem)] font-bold tracking-tight text-[var(--color-text-primary)]">
                {press.contact.title}
              </h2>
              <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-[var(--color-text-secondary)]">
                {press.contact.body}
              </p>
              <a
                href={press.contact.emailHref}
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-[var(--color-accent-base)] px-6 py-3 text-sm font-semibold text-[var(--color-accent-contrast)] transition-colors hover:bg-[var(--color-accent-strong)] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--color-accent-base)]"
              >
                <Mail size={16} aria-hidden />
                {press.contact.emailLabel}
              </a>
            </div>
          </Reveal>
        </Container>
      </section>
    </>
  );
}

export default PressKit;
